/**
 * Put text on the device clipboard, and say honestly whether it landed.
 *
 * Two paths because one is not enough. The async API is the right one and works in
 * Telegram's WebView on both phones, but it is gated on a secure context and a live
 * user gesture, and it rejects rather than degrades. The legacy `execCommand` path
 * needs a real, focused, selectable node — hence the offscreen textarea, and hence the
 * explicit Range: iOS ignores `select()` on a readonly field and would copy nothing at
 * all while reporting success.
 */
export async function copyText(text: string): Promise<boolean> {
  if (!text) return false;

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Blocked or unavailable — fall through rather than give up.
  }

  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.contentEditable = 'true';
    ta.readOnly = false;
    // Kept on screen but invisible: a display:none or zero-sized node cannot hold a
    // selection. 16px because anything smaller makes iOS zoom the page on focus.
    ta.style.cssText =
      'position:fixed;top:0;left:0;width:1px;height:1px;padding:0;border:0;opacity:0;font-size:16px;';
    document.body.appendChild(ta);

    const range = document.createRange();
    range.selectNodeContents(ta);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
    ta.setSelectionRange(0, text.length);

    const ok = document.execCommand('copy');
    sel?.removeAllRanges();
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}
